/**
 * Account's billing state (settings pocket spec §6): the payload from
 * GET /billing/subscription/ mapped to one state, its statement, its one
 * action and the root's plan line. The first matching row wins; the rules
 * mirror backend/apps/billing/access_services.py.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getSubscriptionStatus, type SubscriptionStatus } from "@/api/billing";
import {
  captureDesktopChatAuthScopeGuard,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { formatDate, formatNumber, translate } from "@/i18n";
import { settingsBillingMessages } from "@/i18n/messages/settings-billing";
import { longDate } from "@/lib/long-date";
import { newCheckoutAmount } from "./billing-plan-content";
import { watchBillingRefresh } from "./billing-refresh";

export type AccountPlanState =
  | "preview-pro"
  | "payment-failed"
  | "paused"
  | "trial-payment-unfinished"
  | "canceling"
  | "trial-subscribed"
  | "trial-no-card"
  | "active"
  | "free-trial-available"
  | "free";

export type AccountPlanAction =
  | "update-card"
  | "manage-billing"
  | "keep-pro"
  | "start-trial"
  | "upgrade";

export interface AccountPlan {
  state: AccountPlanState;
  statement: string;
  line: string | null;
  /** Free rows: the projects and apps in use. */
  extraLine: string | null;
  action: { kind: AccountPlanAction; label: string; primary: boolean } | null;
  /** Free rows get “See what Pro adds” and the root's Upgrade button. */
  free: boolean;
  /** The second line of the root's profile block (spec §3). */
  planLine: string;
  /** Red dot on the plan line and the root Account row. */
  paymentFailed: boolean;
}

const DAY_MS = 86_400_000;
const PAYMENT_FAILED = new Set(["past_due", "unpaid", "incomplete"]);
const PAYMENT_UNFINISHED = new Set(["incomplete", "incomplete_expired"]);

const billing = settingsBillingMessages;

function manage() {
  return {
    kind: "manage-billing",
    label: translate(billing, "manageBilling"),
    primary: false,
  } as const;
}

function updateCard(primary: boolean) {
  return {
    kind: "update-card",
    label: translate(billing, "updateCard"),
    primary,
  } as const;
}

function isAhead(iso: string | null | undefined, now: Date): boolean {
  return Boolean(iso) && new Date(iso as string).getTime() > now.getTime();
}

function trialLine(trialEnd: string | null, now: Date): string {
  const days = trialEnd
    ? Math.max(
        1,
        Math.ceil((new Date(trialEnd).getTime() - now.getTime()) / DAY_MS),
      )
    : 1;
  return translate(billing, "trialDaysLeft", { count: days });
}

function monthlyPrice(amount: string): string {
  return translate(billing, "pricePerMonth", { price: amount });
}

/** “€19 a month”, “€199 a year”, or null while Stripe has not reported an amount. */
export function planPrice(
  subscription: SubscriptionStatus["subscription"],
): string | null {
  if (
    !subscription ||
    subscription.amount_cents == null ||
    !subscription.currency
  )
    return null;
  const amount = subscription.amount_cents / 100;
  const price = formatNumber(amount, {
    style: "currency",
    currency: subscription.currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  });
  return subscription.interval === "yearly"
    ? translate(billing, "pricePerYear", { price })
    : monthlyPrice(price);
}

function freeLines(status: SubscriptionStatus, now: Date) {
  const usage = status.usage;
  const limits = status.limits;
  const calls =
    usage?.onscreen_calls_used !== undefined &&
    limits.max_onscreen_calls != null
      ? { used: usage.onscreen_calls_used, limit: limits.max_onscreen_calls }
      : null;
  const resetAt = usage?.onscreen_calls_reset_at;
  const line =
    calls && resetAt
      ? translate(billing, "freeRequestsLine", {
          ...calls,
          date: longDate(resetAt, now, "UTC"),
        })
      : null;
  const extraLine =
    usage && limits.max_projects != null && limits.max_integrations != null
      ? translate(billing, "freeUsageLine", {
          projectsUsed: usage.projects_used,
          projectsLimit: limits.max_projects,
          appsUsed: usage.integrations_used,
          appsLimit: limits.max_integrations,
        })
      : null;
  return {
    line,
    extraLine,
    planLine: calls
      ? translate(billing, "freePlanRequests", calls)
      : translate(billing, "freePlan"),
  };
}

function activeLine(price: string | null, endDate: string | null) {
  if (price && endDate)
    return translate(billing, "activePriceAndCharge", { price, date: endDate });
  if (price) return translate(billing, "activePrice", { price });
  return endDate ? translate(billing, "nextCharge", { date: endDate }) : null;
}

/** The plan statement, action and lines; translated, so call it at render time. */
export function accountPlan(
  status: SubscriptionStatus,
  now: Date = new Date(),
): AccountPlan {
  const subscription = status.subscription;
  const subStatus = subscription?.status ?? null;
  const trialing = status.access_status === "trialing";
  const awaitingFirstCharge =
    subStatus === "active" &&
    subscription?.interval === "monthly" &&
    status.trial_started_at !== null &&
    isAhead(status.trial_end, now);
  const endIso =
    trialing || awaitingFirstCharge
      ? status.trial_end
      : (subscription?.current_period_end ?? null);
  const date = endIso
    ? longDate(endIso, now)
    : translate(billing, "endOfPeriod");
  const base = {
    line: null,
    extraLine: null,
    free: false,
    paymentFailed: false,
  };

  if (status.preview_pro_grant) {
    return {
      ...base,
      state: "preview-pro",
      statement: translate(billing, "previewProStatement"),
      line: translate(billing, "previewProLine"),
      action: status.has_billing_customer
        ? subStatus && PAYMENT_FAILED.has(subStatus)
          ? updateCard(false)
          : manage()
        : null,
      planLine: translate(billing, "previewProPlanLine"),
    };
  }

  if (subStatus && PAYMENT_FAILED.has(subStatus) && !status.has_pro_access) {
    return {
      ...base,
      state: "payment-failed",
      statement: translate(billing, "paymentFailedStatement"),
      line: translate(billing, "paymentFailedLine"),
      action: updateCard(true),
      planLine: translate(billing, "paymentFailedPlanLine"),
      paymentFailed: true,
    };
  }
  if (subStatus === "paused") {
    return {
      ...base,
      state: "paused",
      statement: translate(billing, "pausedStatement"),
      line: translate(billing, "pausedLine"),
      action: manage(),
      planLine: translate(billing, "pausedPlanLine"),
    };
  }
  if (trialing && subStatus && PAYMENT_UNFINISHED.has(subStatus)) {
    return {
      ...base,
      state: "trial-payment-unfinished",
      statement: translate(billing, "tryingProStatement"),
      line: translate(billing, "trialUnfinishedLine", { date }),
      action: manage(),
      planLine: trialLine(status.trial_end, now),
    };
  }
  if (status.has_pro_access && subscription?.cancel_at_period_end) {
    return {
      ...base,
      state: "canceling",
      statement: translate(billing, "proEndsStatement", { date }),
      line: translate(billing, "cancelingLine"),
      action: manage(),
      planLine: endIso
        ? translate(billing, "proUntil", {
            date: formatDate(endIso, { month: "short", day: "numeric" }),
          })
        : translate(billing, "proPlan"),
    };
  }
  if ((trialing && subStatus === "trialing") || awaitingFirstCharge) {
    const price = planPrice(subscription);
    return {
      ...base,
      state: "trial-subscribed",
      statement: translate(billing, "tryingProStatement"),
      line: price
        ? translate(billing, "trialEndsThenPrice", { date, price })
        : translate(billing, "trialEnds", { date }),
      action: manage(),
      planLine: trialLine(status.trial_end, now),
    };
  }
  if (trialing && !subscription) {
    return {
      ...base,
      state: "trial-no-card",
      statement: translate(billing, "tryingProStatement"),
      line: translate(billing, "trialEndsNoCharge", { date }),
      action: {
        kind: "keep-pro",
        label: translate(billing, "keepProFor", {
          price: monthlyPrice(newCheckoutAmount()),
        }),
        primary: true,
      },
      planLine: trialLine(status.trial_end, now),
    };
  }
  if (status.access_status === "active") {
    return {
      ...base,
      state: "active",
      statement: translate(billing, "onProStatement"),
      line: activeLine(planPrice(subscription), endIso ? date : null),
      action: manage(),
      planLine: translate(billing, "proPlan"),
    };
  }
  const free = freeLines(status, now);
  if (status.trial_eligible) {
    return {
      ...base,
      ...free,
      free: true,
      state: "free-trial-available",
      statement: translate(billing, "onFreeStatement"),
      action: {
        kind: "start-trial",
        label: translate(billing, "tryProFree"),
        primary: true,
      },
    };
  }
  return {
    ...base,
    ...free,
    free: true,
    state: "free",
    statement: translate(billing, "onFreeStatement"),
    action: {
      kind: "upgrade",
      label: translate(billing, "upgradeFor", {
        price: monthlyPrice(newCheckoutAmount()),
      }),
      primary: true,
    },
  };
}

/** The billing payload, loaded once and again after Stripe returns or billing changes. */
export function useSubscriptionStatus() {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const request = ++generation.current;
    const scope = captureDesktopChatAuthScopeGuard();
    try {
      const loaded = await getSubscriptionStatus();
      if (request !== generation.current || !scope()) return;
      setStatus(loaded);
      setFailed(false);
    } catch {
      if (request !== generation.current || !scope()) return;
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    void reload();
    const unsubscribe = watchBillingRefresh(reload);
    const unsubscribeAuth = subscribeDesktopChatAuthScope(() => {
      setStatus(null);
      void reload();
    });
    return () => {
      generation.current++;
      unsubscribe();
      unsubscribeAuth();
    };
  }, [reload]);
  return { status, failed, reload, setStatus };
}
